import { graphql } from "gatsby";
import React from "react";
import { twJoin } from "tailwind-merge";

import Breakpoint from "../breakpoint";
import Image from "../image";
import RichText from "../richText";

type Props = {
  slice:
    | Queries.TreatmentImageWithTextSliceFragment
    | Queries.TreatmentAddonImageWithTextSliceFragment
    | Queries.MarketingTreatmentImageWithTextSliceFragment
    | Queries.ContentImageWithTextSliceFragment
    | Queries.DentistImageWithTextSliceFragment
    | Queries.LocationImageWithTextSliceFragment
    | Queries.ProductImageWithTextSliceFragment;
};

const ImageWithText: React.FC<Props> = ({ slice }) => {
  const { primary } = slice;
  const { title, text, side, imageDesktop, imageMobile, bgGrey, margins } = primary;

  const imgMobile = <Image image={imageMobile} className="w-full aspect-[4/3]" />;
  const imgDesktop = <Image image={imageDesktop} className="w-full aspect-[5/4] max-h-[528px]" />;

  return (
    <section className={twJoin("px-5", bgGrey ? "bg-neutral-50" : "bg-white")}>
      <div
        className={twJoin(
          "container px-0 max-w-screen-xl grid mdlg:grid-cols-2 gap-5 mdlg:gap-10 lg:gap-[70px]",
          margins === "Only bottom" && "p-b-xxxl-fluid",
          margins === "Only top" && "p-t-xxxl-fluid",
          margins === "Top and bottom" && "p-b-xxxl-fluid p-t-xxxl-fluid",
        )}
      >
        <div
          className={twJoin(
            "row-start-1 overflow-hidden rounded-lg empty:hidden mdlg:row-start-auto",
            side === "left" ? "mdlg:order-0" : "mdlg:order-1",
          )}
        >
          <Breakpoint mobile={imgMobile} desktop={imgDesktop} breakpoint="md" />
        </div>
        <div
          className={twJoin(
            "self-center lg:max-w-[565px]",
            side === "left" ? "mdlg:justify-self-start" : "mdlg:justify-self-end",
          )}
        >
          <RichText data={title?.richText} className="block m-b-sm-fluid" />
          <RichText data={text?.richText} className="block typo-base unstyled-list text-neutral-600" />
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment TreatmentImageWithTextSlice on PrismicTreatmentDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment TreatmentAddonImageWithTextSlice on PrismicTreatmentAddonDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment MarketingTreatmentImageWithTextSlice on PrismicMarketingTreatmentDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment ContentImageWithTextSlice on PrismicPageContentDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        url
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment DentistImageWithTextSlice on PrismicPageDentistDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        url
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment LocationImageWithTextSlice on PrismicLocationDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }

  fragment ProductImageWithTextSlice on PrismicProductDataBodyImageWithText {
    ...Slice

    primary {
      title: image_title {
        ...RichText
      }
      text: image_text {
        ...RichText
      }
      side: image_side
      imageDesktop: image_image {
        alt
        gatsbyImageData(height: 765, placeholder: BLURRED)
      }
      imageMobile: image_image {
        alt
        gatsbyImageData(height: 450, placeholder: BLURRED)
      }
      bgGrey: image_background_color
      margins
    }
  }
`;

export default ImageWithText;
